/**
 * Builds a Set of lowercase selection labels across enabled terms.
 *
 * The chip-link in the NLP modal can store the same synonym/plural under multiple
 * source terms simultaneously, and OR-sibling terms often share large overlapping
 * lists. Counting the raw `selectedByTerm[term].length` sums double-counts those.
 * This helper collapses to the distinct underlying strings — the count of unique
 * things that will actually appear in the final Lucene query.
 *
 * @param {Record<string, string[]> | undefined | null} selectedByTerm
 * @param {Record<string, boolean> | undefined | null} enabledByTerm
 * @returns {Set<string>} lowercased distinct labels
 */
export function selectedDistinctLowers(selectedByTerm, enabledByTerm) {
  const out = new Set();
  if (!selectedByTerm) return out;
  for (const [term, labels] of Object.entries(selectedByTerm)) {
    if (!enabledByTerm?.[term]) continue;
    if (!Array.isArray(labels)) continue;
    for (const label of labels) {
      if (label) out.add(label.toLowerCase());
    }
  }
  return out;
}

/**
 * Returns deduped synonym + plural selection dictionaries so each selected label
 * is attributed to exactly one sibling within an OR-group. The first sibling in
 * each group keeps the label; subsequent siblings have it removed. A shared
 * "seen" set spans both synonyms AND plurals within the group so the same string
 * never appears twice in the final Lucene query.
 *
 * Siblings outside an OR-group of size >= 2 are untouched. AND-coupled terms
 * stay independent (the backend only emits groups of OR-siblings).
 *
 * @param {Record<string, string[]>} effectiveSyn
 * @param {Record<string, string[]>} effectivePlu
 * @param {string[][] | undefined | null} orGroups
 * @returns {{ syn: Record<string, string[]>, plu: Record<string, string[]> }}
 */
export function dedupSelectionsWithinOrGroups(
  effectiveSyn,
  effectivePlu,
  orGroups
) {
  const syn = { ...(effectiveSyn || {}) };
  const plu = { ...(effectivePlu || {}) };
  if (!Array.isArray(orGroups) || orGroups.length === 0) {
    return { syn, plu };
  }

  for (const group of orGroups) {
    if (!Array.isArray(group) || group.length < 2) continue;
    // Seed `seen` with the OR-sibling terms themselves so an exact duplicate of
    // a sibling (case mismatch that escaped backend dedup) never re-emerges.
    const seen = new Set(group.map(t => t.toLowerCase()));

    const keepUnseen = labels =>
      (labels || []).filter(label => {
        const k = label?.toLowerCase();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });

    for (const term of group) {
      if (syn[term]) syn[term] = keepUnseen(syn[term]);
      if (plu[term]) plu[term] = keepUnseen(plu[term]);
    }
  }

  return { syn, plu };
}
